<template>
    <card title="AR Statements">
        <b-container fluid>
            <div v-if="!arEnabled" class="text-center p-3 text-muted">
                Accounts Receivable is not enabled for this pharmacy.
            </div>
            <template v-else>
                <b-row class="align-items-end mb-2">
                    <b-col cols="3">
                        <label class="mb-1">Period Start</label>
                        <el-date-picker v-model="periodStart" type="date" format="MM/dd/yyyy"
                            value-format="yyyy-MM-dd" style="width: 100%" />
                    </b-col>
                    <b-col cols="3">
                        <label class="mb-1">Period End</label>
                        <el-date-picker v-model="periodEnd" type="date" format="MM/dd/yyyy"
                            value-format="yyyy-MM-dd" style="width: 100%" />
                    </b-col>
                    <b-col cols="3">
                        <label class="mb-1">Due By</label>
                        <el-date-picker v-model="dueByDate" type="date" format="MM/dd/yyyy"
                            value-format="yyyy-MM-dd" style="width: 100%" />
                    </b-col>
                    <b-col cols="3" class="text-right">
                        <b-button v-if="canRunBatch" variant="primary" :disabled="working" @click="runBatch">
                            Run Monthly Batch
                        </b-button>
                    </b-col>
                </b-row>

                <hr />

                <b-row class="align-items-end mb-2">
                    <b-col cols="3">
                        <label class="mb-1">AR Account ID</label>
                        <b-form-input v-model="accountId" type="text" inputmode="numeric"
                            :formatter="digitsOnly" />
                    </b-col>
                    <b-col cols="9" class="text-right">
                        <b-button variant="outline-secondary" class="mr-2" :disabled="!hasValidAccountId || working"
                            @click="loadStatements">
                            Load Statements
                        </b-button>
                        <b-button v-if="canGenerate" variant="primary" :disabled="!hasValidAccountId || working"
                            @click="generateStatement">
                            Generate Statement
                        </b-button>
                    </b-col>
                </b-row>

                <b-table :fields="fields" :items="statements" :busy="loading" small responsive show-empty>
                    <template #table-busy>
                        <div class="text-center my-2"><b-spinner small /> Loading...</div>
                    </template>
                    <template #empty>No statements for this account.</template>
                </b-table>
            </template>
        </b-container>
    </card>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { ARStatementSummary } from "@/models/AR";
import { DatePicker } from "element-ui";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "ARStatementsPage",
    components: {
        [DatePicker.name]: DatePicker,
    },
})
export default class ARStatementsPage extends Vue {
    private arEnabled = false;
    private loading = false;
    private working = false;
    // Kept as a string so leading zeros survive typing (a number input coerces "0123" -> 123).
    // Only ever interpolated into request URLs, so a digit string works everywhere it's used.
    private accountId = "";
    private statements: ARStatementSummary[] = [];

    private periodStart: string = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
    private periodEnd: string = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
    private dueByDate: string = moment().add(30, "days").format("YYYY-MM-DD");

    get canRunBatch(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Statement/Run Batch");
    }

    get canGenerate(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Statement/Generate Single");
    }

    // A digit string means "0"/"000" are truthy, so guard on the numeric value: only a positive
    // account id is a real account (never request /AR/accounts/0/... for a non-existent account).
    get hasValidAccountId(): boolean {
        return Number(this.accountId) > 0;
    }

    get fields() {
        const money = (v: number) => this.currency(v);
        const date = (v: string) => this.formatDate(v);
        return [
            { key: "statementDate", label: "Statement Date", formatter: date },
            { key: "periodStart", label: "Period Start", formatter: date },
            { key: "periodEnd", label: "Period End", formatter: date },
            { key: "endingBalance", label: "Ending Balance", formatter: money },
            { key: "dueByDate", label: "Due By", formatter: date },
            { key: "status", label: "Status", formatter: (v: number) => (v === 2 ? "Delivered" : "Generated") },
        ];
    }

    async mounted(): Promise<void> {
        try {
            const response = await axios.get<boolean>("/configuration/enableAR");
            this.arEnabled = response.data === true;
        } catch {
            this.arEnabled = false;
        }
    }

    async loadStatements(): Promise<void> {
        if (!this.hasValidAccountId) return;
        try {
            this.loading = true;
            const response = await axios.get<ARStatementSummary[]>(`/AR/accounts/${this.accountId}/statements`);
            this.statements = response.data ?? [];
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            this.statements = [];
        } finally {
            this.loading = false;
        }
    }

    async generateStatement(): Promise<void> {
        if (!this.hasValidAccountId) return;
        try {
            this.working = true;
            await axios.post(`/AR/accounts/${this.accountId}/statements`, {
                periodStart: this.periodStart,
                periodEnd: this.periodEnd,
                dueByDate: this.dueByDate,
            });
            this.$notification(NotificationOptions.success("Statement generated."));
            await this.loadStatements();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.working = false;
        }
    }

    async runBatch(): Promise<void> {
        try {
            this.working = true;
            const response = await axios.post<number>("/AR/statements/run-batch", {
                periodStart: this.periodStart,
                periodEnd: this.periodEnd,
                dueByDate: this.dueByDate,
            });
            this.$notification(NotificationOptions.success(`Batch generated ${response.data ?? 0} statement(s).`));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.working = false;
        }
    }

    digitsOnly(value: string): string {
        return (value || "").replace(/\D/g, "");
    }

    currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }

    formatDate(date?: string): string {
        return date ? moment.utc(date).local().format("L") : "";
    }
}
</script>

<style scoped></style>
